// Módulo puro de captura de Parámetros UTM — espejo de src/lib/referral.ts.
// Sin dependencias de React ni de Supabase. Todas las operaciones de
// localStorage van envueltas en try/catch y devuelven un valor de respaldo
// documentado (Requisito 2.6): esta biblioteca nunca debe romper la página.

const STORAGE_KEY = 'lia_utm_first_touch';
const CLAIMED_KEY_PREFIX = 'lia_utm_claimed_uid';
export const ATTRIBUTION_WINDOW_DAYS = 30; // mismo TTL que el Sistema de Referidos
const MAX_VALUE_LEN = 200;
const MAX_PATH_LEN = 300;

/** Identificador de visitante: se reutiliza el del Sistema de Referidos (clave lia_visitor_id). */
export { getVisitorId } from './referral';

export interface UtmParams {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
}

export interface StoredAttribution {
  params: UtmParams;
  landingPath: string;
  ts: number; // Fecha de Primera Llegada (epoch ms)
}

/** trim → minúsculas → truncado a 200; '' y whitespace puro → null. */
export function normalizeUtmValue(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim().toLowerCase().slice(0, MAX_VALUE_LEN);
  return trimmed.length > 0 ? trimmed : null;
}

/** Extrae los 5 parámetros de la cadena de consulta. Depende SOLO de `search`. */
export function parseUtmFromSearch(search: string): UtmParams {
  const params = new URLSearchParams(search);
  return {
    source: normalizeUtmValue(params.get('utm_source')),
    medium: normalizeUtmValue(params.get('utm_medium')),
    campaign: normalizeUtmValue(params.get('utm_campaign')),
    content: normalizeUtmValue(params.get('utm_content')),
    term: normalizeUtmValue(params.get('utm_term')),
  };
}

export function hasAnyUtm(p: UtmParams): boolean {
  return !!(p.source || p.medium || p.campaign || p.content || p.term);
}

/** Clave de deduplicación en memoria: params normalizados + ruta. */
export function touchKey(p: UtmParams, landingPath: string): string {
  return [p.source, p.medium, p.campaign, p.content, p.term, landingPath]
    .map((v) => v ?? '')
    .join('|');
}

function normalizeLandingPath(pathname: string): string {
  return (pathname || '').slice(0, MAX_PATH_LEN);
}

/** Primer toque almacenado si no ha expirado; si expiró, lo borra y devuelve null. */
export function getStoredAttribution(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (!parsed?.params || typeof parsed.ts !== 'number') return null;
    const ageDays = (Date.now() - parsed.ts) / 86_400_000;
    if (ageDays > ATTRIBUTION_WINDOW_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Si la URL trae ≥1 UTM: devuelve el toque y, cuando no hay primer toque vigente,
 * lo escribe en el Almacenamiento Local de Atribución. Si ya lo hay, NO lo toca.
 * Devuelve null cuando la URL no trae ningún UTM.
 */
export function captureUtmFromUrl(
  search: string,
  pathname: string,
): { params: UtmParams; landingPath: string; isFirstTouch: boolean } | null {
  const params = parseUtmFromSearch(search);
  if (!hasAnyUtm(params)) return null;

  const landingPath = normalizeLandingPath(pathname);
  const existing = getStoredAttribution();
  if (existing) {
    return { params: existing.params, landingPath: existing.landingPath, isFirstTouch: false };
  }

  try {
    const payload: StoredAttribution = { params, landingPath, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable: continúa sin persistir el primer toque */
  }

  return { params, landingPath, isFirstTouch: true };
}

/** Marcador best-effort para no repetir el reclamo en cada carga. No es autoritativo. */
export function markClaimed(userId: string): void {
  try {
    localStorage.setItem(`${CLAIMED_KEY_PREFIX}_${userId}`, '1');
  } catch {
    /* noop */
  }
}

export function isClaimed(userId: string): boolean {
  try {
    return localStorage.getItem(`${CLAIMED_KEY_PREFIX}_${userId}`) === '1';
  } catch {
    return false;
  }
}
